import { useState, useEffect } from 'react';
import './styles.css';

// Import images
import nightTrainingImg from './assets/images/nighttraining.jpg';
import cratosImg from './assets/images/USML_CRATOS2.png';
import hadesImg from './assets/images/USML_HADES.png';
import sableImg from './assets/images/USML_SABLE.png';
import helmetLogoImg from './assets/images/usmlcascoblancochico.png';

// Import members data
import membersData from './assets/members.json';

// Gallery images
import kivuImg1 from './assets/images/grid/Kivu 1.jpg';
import kivuImg2 from './assets/images/grid/Kivu 2.jpg';
import napfImg1 from './assets/images/grid/Napf 1 square.png';
import napfImg2 from './assets/images/grid/Napf 2 square.jpg';
import rosarioImg1 from './assets/images/grid/Rosario 1 square.jpg';
import rosarioImg2 from './assets/images/grid/Rosario 2 square.jpg';
import escudoImg1 from './assets/images/grid/Escudo de Paz 1 square.jpg';
import escudoImg2 from './assets/images/grid/Escudo de Paz 2 square.jpg';

// Member images
// import ghostImg from './assets/images/members/Ghost.jpg';
// import kombuchaImg from './assets/images/members/Kombuchaman.jpg';
// import ponceImg from './assets/images/members/Ponce.jpg';
// import userkaftImg from './assets/images/members/Userkaft.jpg';
// import deknopImg from './assets/images/members/Deknop.png';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState('Todos');

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  // Get branch stats
  const getBranchStats = () => {
    const branches = {};
    membersData.forEach(member => {
      const branch = member.branch;
      branches[branch] = (branches[branch] || 0) + 1;
    });
    return branches;
  };

  // Get active members organized by rank hierarchy
  const getHierarchyData = () => {
    const activeMembers = membersData.filter(member => member.status);
    
    // Define rank hierarchy order (highest to lowest) for GROUND FORCES (Sable + Cratos)
    const groundRankHierarchy = [
      'Sargento',
      'Cabo',
      'Soldado de 1ra',
      'Soldado de 2da',
      'Soldado',
      'Cadete',
      'Recluta',
      'Aspirante'
    ];

    // Define rank hierarchy order for AIR FORCE (Hades)
    const airRankHierarchy = [
      'Senior Airman',
      'Airman First Class',
      'Airman',
      'Cadete',
      'Recluta',
      'Aspirante'
    ];

    const groundHierarchy = {};
    const airHierarchy = {};

    // Build ground forces hierarchy (Sable + Cratos)
    groundRankHierarchy.forEach(rank => {
      const membersInRank = activeMembers.filter(member =>
        member.rank === rank && (member.branch === 'Sable' || member.branch === 'Cratos')
      );
      if (membersInRank.length > 0) {
        groundHierarchy[rank] = membersInRank;
      }
    });

    // Build air force hierarchy (Hades)
    airRankHierarchy.forEach(rank => {
      const membersInRank = activeMembers.filter(member =>
        member.rank === rank && member.branch === 'Hades'
      );
      if (membersInRank.length > 0) {
        airHierarchy[rank] = membersInRank;
      }
    });

    return { ground: groundHierarchy, air: airHierarchy };
  };

  return (
    <div className="App">
      {/* Navigation */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="navbar-container">
          <a href="#home" className="logo" onClick={() => scrollToSection('home')}>
            <img src={helmetLogoImg} alt="USML Helmet" className="logo-helmet" />
            <span>USML</span>
          </a>
          <ul className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
            <li><a href="#home" onClick={() => scrollToSection('home')}>Inicio</a></li>
            <li><a href="#about" onClick={() => scrollToSection('about')}>Nosotros</a></li>
            <li><a href="#branches" onClick={() => scrollToSection('branches')}>Ramas</a></li>
            <li><a href="#gallery" onClick={() => scrollToSection('gallery')}>Galería</a></li>
            <li><a href="#members" onClick={() => scrollToSection('members')}>Miembros</a></li>
            <li><a href="#contact" onClick={() => scrollToSection('contact')}>Unirse</a></li>
          </ul>
          <button className="mobile-menu-toggle" onClick={toggleMenu}>
            ☰
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="container">
          <div className="hero-content fade-in-up">
            <h1>Unidad de Simulación Militar Latina</h1>
            <p className="hero-subtitle">Clan de Arma & Comunidad Gamer</p>
            <p className="hero-description">
              Bienvenido a la USML. Somos una comunidad de simulación militar que toma en serio el trabajo en equipo y la inmersión táctica, pero sin perder de vista que estamos aquí para pasarla bien. Si te interesa el realismo, la disciplina y formar parte de algo más grande, este es tu lugar.
            </p>
            <div className="cta-buttons">
              <a href="#about" className="btn btn-primary" onClick={() => scrollToSection('about')}>
                Conocer Más
              </a>
              <a href="#contact" className="btn btn-secondary" onClick={() => scrollToSection('contact')}>
                Únete
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about section-padding">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>Sobre la <span className="text-accent">USML</span></h2>
              <p>
                La Unidad de Simulación Militar Latina representa la excelencia en operaciones milsim. Fundada desde Arma 2 y consolidada en Arma 3, tiene su base fundamental en los principios de hermandad, disciplina, superioridad táctica y estratégica. Operamos como una fuerza de elite a través de múltiples plataformas de simulación militar.
              </p>
              <p>
                Nuestros miembros pasan por entrenamientos de calidad y mantienen los más altos estándares de preparación para combates en simulación militar. La experiencia acumulada en distintos ambientes de combate durante todos estos años nos ha otorgado una capacidad operativa única para la adaptación inmediata de nuestras unidades en cualquier área de operaciones.
              </p>
              <p>
                USML entrega resultados a través de ejecución precisa y trabajo en equipo inquebrantable. Ya sea participando en operaciones a distintos niveles y escalas o en misiones especializadas, nuestra comunidad está lista para enfrentar cualquier desafío con excelencia táctica y disciplina militar.
              </p>
            </div>
            <div className="about-image">
              <img src={nightTrainingImg} alt="USML Night Training Operations" />
            </div>
          </div>
        </div>
      </section>

      {/* Branches Section */}
      <section id="branches" className="features section-padding">
        <div className="container">
          <div className="text-center">
            <h2>Ramas operativas de la <span className="text-accent">USML</span></h2>
            <p>Nuestras divisiones especializadas, cada una con sus propias tradiciones y expertise</p>
          </div>
          <div className="branches-grid">
            <div className="branch-card">
              <div className="branch-patch">
                <img src={sableImg} alt="Rama Sable" />
              </div>
              <h3>SABLE</h3>
              <p>Infantería Aerotransportada especializada en paracaidismo, columna vertebral de USML en el área de operaciones. Fundada sobre los principios del 75.º Regimiento Ranger, es la infantería de despliegue rápido con entrenamiento completo en especializaciones de mayor requerimiento y profundidad. Conformada por personal con experiencia probada y trayectoria en el clan.</p>
              <div className="branch-stats">
                <span>{getBranchStats()['Sable'] || 0} miembros</span>
              </div>
            </div>
            <div className="branch-card">
              <div className="branch-patch">
                <img src={hadesImg} alt="Rama Hades" />
              </div>
              <h3>HADES</h3>
              <p>Fuerza de apoyo aéreo elite de USML. Dominan los cielos con precisión y velocidad, conformados por pilotos con certificación completa en ala fija y rotativa. Especialistas en operaciones aéreas que operan roles de transporte de tropas, logística y apoyo aéreo cercano a las unidades desplegadas en tierra.</p>
              <div className="branch-stats">
                <span>{getBranchStats()['Hades'] || 0} miembros</span>
              </div>
            </div>
            <div className="branch-card">
              <div className="branch-patch">
                <img src={cratosImg} alt="Rama Cratos" />
              </div>
              <h3>CRATOS</h3>
              <p>Infantería de choque, fuerza de apoyo terrestre de USML a disposición de Sable en el área de operaciones. El personal recibe entrenamiento de infantería y desarrolla especializaciones propias dentro de la rama. Con experiencia y dedicación, pueden ascender internamente o progresar hacia SABLE o HADES.</p>
              <div className="branch-stats">
                <span>{getBranchStats()['Cratos'] || 0} miembros</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="stats section-padding">
        <div className="container">
          <div className="text-center">
            <h2>USML en <span className="text-accent">Números</span></h2>
            <p>Nuestra fortaleza se refleja en estos datos</p>
          </div>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{membersData.length}</div>
              <div className="stat-label">Miembros totales</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{membersData.filter(m => m.status).length}</div>
              <div className="stat-label">Miembros Activos</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">2013</div>
              <div className="stat-label">Año de Fundación</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">29</div>
              <div className="stat-label">Operaciones (USMLOPS)</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">0</div>
              <div className="stat-label">Días desde el último accidente 😂</div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="gallery section-padding">
        <div className="container">
          <div className="text-center">
            <h2>Galería de <span className="text-accent">Misiones</span></h2>
            <p>Momentos destacados de nuestras operaciones tácticas en varios teatros</p>
          </div>
          <div className="gallery-grid">
            <div className="gallery-item">
              <img src={kivuImg1} alt="Operación Kivu Alpha" />
              <div className="gallery-overlay">
                <h4>Operación Kivu</h4>
              </div>
            </div>
            <div className="gallery-item">
              <img src={kivuImg2} alt="Operación Kivu Bravo" />
              <div className="gallery-overlay">
                <h4>Operación Kivu</h4>
              </div>
            </div>
            <div className="gallery-item">
              <img src={napfImg1} alt="Operación Napf" />
              <div className="gallery-overlay">
                <h4>Operación Napf</h4>
              </div>
            </div>
            <div className="gallery-item">
              <img src={napfImg2} alt="Evaluación Táctica Napf" />
              <div className="gallery-overlay">
                <h4>Operación Napf</h4>
              </div>
            </div>
            <div className="gallery-item">
              <img src={rosarioImg1} alt="Campaña Rosario" />
              <div className="gallery-overlay">
                <h4>Campaña Rosario</h4>
              </div>
            </div>
            <div className="gallery-item">
              <img src={rosarioImg2} alt="Consecuencias Rosario" />
              <div className="gallery-overlay">
                <h4>Campaña Rosario</h4>
              </div>
            </div>
            <div className="gallery-item">
              <img src={escudoImg1} alt="Escudo de Paz" />
              <div className="gallery-overlay">
                <h4>Operación Escudo de Paz</h4>
              </div>
            </div>
            <div className="gallery-item">
              <img src={escudoImg2} alt="Operación Escudo de Paz" />
              <div className="gallery-overlay">
                <h4>Operación Escudo de Paz</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Parliament Section */}
      {/* <section id="parliament" className="members section-padding">
        <div className="container">
          <div className="text-center">
            <h2>Nuestro <span className="text-accent">Parlamento</span></h2>
            <p>El equipo administrativo del clan, grupo de personas que desinteresadamente aportan su granito de arena para el correcto funcionamiento del clan. Esta figura no aporta rango in-game.</p>
          </div>
          <div className="members-grid">
            <div className="member-card">
              <div className="member-avatar">
                <img src={userkaftImg} alt="Userkaft" />
              </div>
              <div className="member-name">Userkaft</div>
              <div className="member-rank">Administrador</div>
            </div>
            <div className="member-card">
              <div className="member-avatar">
                <img src={ponceImg} alt="Ponce" />
              </div>
              <div className="member-name">Ponce</div>
              <div className="member-rank">Administrador</div>
            </div>
            <div className="member-card">
              <div className="member-avatar">
                <img src={ghostImg} alt="Ghost" />
              </div>
              <div className="member-name">Ghost</div>
              <div className="member-rank">Administrador</div>
            </div>
            <div className="member-card">
              <div className="member-avatar">
                <img src={deknopImg} alt="Deknop" />
              </div>
              <div className="member-name">Deknop</div>
              <div className="member-rank">Administrador</div>
            </div>
            <div className="member-card">
              <div className="member-avatar">
                <img src={kombuchaImg} alt="Kombuchaman" />
              </div>
              <div className="member-name">Kombuchaman</div>
              <div className="member-rank">Administrador</div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Military Hierarchy Section */}
      <section id="members" className="military-hierarchy section-padding">
        <div className="container">
          <div className="text-center">
            <h2>Malla <span className="text-accent">USML</span></h2>
            <p>Estructura jerárquica in-game de miembros en servicio activo</p>
          </div>
          
          {/* Branch Filter */}
          <div className="branch-filter">
            <button 
              className={`filter-btn ${selectedBranch === 'Todos' ? 'active' : ''}`}
              onClick={() => setSelectedBranch('Todos')}
            >
              Todos ({membersData.filter(m => m.status).length})
            </button>
            <button 
              className={`filter-btn ${selectedBranch === 'Sable' ? 'active' : ''}`}
              onClick={() => setSelectedBranch('Sable')}
            >
              Sable ({membersData.filter(m => m.status && m.branch === 'Sable').length})
            </button>
            <button 
              className={`filter-btn ${selectedBranch === 'Hades' ? 'active' : ''}`}
              onClick={() => setSelectedBranch('Hades')}
            >
              Hades ({membersData.filter(m => m.status && m.branch === 'Hades').length})
            </button>
            <button 
              className={`filter-btn ${selectedBranch === 'Cratos' ? 'active' : ''}`}
              onClick={() => setSelectedBranch('Cratos')}
            >
              Cratos ({membersData.filter(m => m.status && m.branch === 'Cratos').length})
            </button>
          </div>

          {/* Hierarchy Chart - Two Column Layout */}
          <div className="hierarchy-chart-wrapper">
            {/* Ground Forces Column (Sable + Cratos) */}
            {selectedBranch !== 'Hades' && (
            <div className="hierarchy-column">
              <div className="column-header">
                <h3 className="column-title">Malla Terrestre</h3>
                <p className="column-subtitle">Sable & Cratos</p>
              </div>
              <div className="hierarchy-chart">
                {Object.entries(getHierarchyData().ground).map(([rank, members]) => {
                  // Filter by branch if selected
                  const filteredMembers = selectedBranch === 'Todos'
                    ? members
                    : members.filter(member => member.branch === selectedBranch);

                  if (filteredMembers.length === 0) return null;

                  return (
                    <div key={rank} className="rank-tier">
                      <div className="rank-header">
                        <h3 className="rank-title">{rank}</h3>
                      </div>
                      <div className="members-row">
                        {filteredMembers.map((member, index) => (
                          <div key={index} className="member-chip">
                            <span className="member-name">{member.name}</span>
                            <span className={`branch-indicator branch-${member.branch.toLowerCase()}`}>
                              {member.branch}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            )}

            {/* Air Force Column (Hades) */}
            {selectedBranch !== 'Sable' && selectedBranch !== 'Cratos' && (
            <div className="hierarchy-column">
              <div className="column-header">
                <h3 className="column-title">Malla Aérea</h3>
                <p className="column-subtitle">Hades</p>
              </div>
              <div className="hierarchy-chart">
                {Object.entries(getHierarchyData().air).map(([rank, members]) => {
                  // Filter by branch if selected
                  const filteredMembers = selectedBranch === 'Todos'
                    ? members
                    : members.filter(member => member.branch === selectedBranch);

                  if (filteredMembers.length === 0) return null;

                  return (
                    <div key={rank} className="rank-tier">
                      <div className="rank-header">
                        <h3 className="rank-title">{rank}</h3>
                      </div>
                      <div className="members-row">
                        {filteredMembers.map((member, index) => (
                          <div key={index} className="member-chip">
                            <span className="member-name">{member.name}</span>
                            <span className={`branch-indicator branch-${member.branch.toLowerCase()}`}>
                              {member.branch}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact section-padding">
        <div className="container">
          <div className="text-center">
            <h2>Únete a la <span className="text-accent">Unidad</span></h2>
            <p>Conectá con nosotros en Discord para comenzar tu proceso de alistamiento.</p>
          </div>
          
          <div className="contact-content">
            <div className="contact-info">
              <h3>Proceso de Reclutamiento</h3>
              <div className="requirement-item">
                <h4>Requisitos Básicos:</h4>
                <ul>
                  <li>Ser mayor de 18 años</li>
                  <li>Contar con el juego original en steam</li>
                  <li>Contar con micrófono funcional</li>
                  <li>Disponibilidad para los entrenamientos básicos</li>
                  <li>Actitud respetuosa y amigable</li>
                </ul>
              </div>
              <div className="requirement-item">
                <h4>Proceso:</h4>
                <ol>
                  <li>Únete a nuestro Discord</li>
                  <li>Presenta tu solicitud en el canal correspondiente</li>
                  <li>Te contactaremos y tendremos una charla contigo</li>
                  <li>Completa los 4 entrenamientos básicos</li>
                  <li>¡Bienvenido a la USML!</li>
                </ol>
              </div>
            </div>
            
            <div className="discord-widget">
              <h3>Únete a nuestro Discord</h3>
              <p>Servidor oficial de la Unidad de Simulación Militar Latina</p>
              {/* Discord Widget */}
              <div className="discord-embed">
                <iframe 
                  src="https://discord.com/widget?id=910149518538989588&theme=dark" 
                  width="350" 
                  height="500" 
                  allowtransparency="true" 
                  frameborder="0" 
                  sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
                  title="Discord Server Widget - USML"
                ></iframe>
              </div>
              <div className="discord-backup">
                <a href="https://discord.gg/4PcYVxGrNk" className="btn btn-primary" target="_blank" rel="noopener noreferrer">
                  Unirse al Discord
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4>USML</h4>
              <p>
                Velocidad, Sorpresa y Violencia.
              </p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Unidad de Simulación Militar Latina. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;